import { Button } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import React, { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  navigateWithMobileDashboardScroll,
  primeMobileDashboardContentScroll,
} from "../../utils/navigateWithMobileDashboardScroll";
import { getAllOrdersOfShop } from "../../redux/actions/order";
import { AiOutlineArrowRight } from "react-icons/ai";
import { useNotifications } from "../../hooks/useNotifications";
import { useMarkNotificationsReadOnPage } from "../../hooks/useMarkNotificationsReadOnPage";
import { ORDER_NOTIFICATION_TYPES, SELLER_REFUND_REVIEW_TYPES } from "../../utils/notificationTypes";
import {
  getNotificationRelatedOrderId,
  isSellerFacingOrderNotification,
} from "../../utils/orderNotificationMatch";
import {
  getOrderListStatusLabel,
  isOrderListStatusSuccess,
} from "../../utils/orderListStatus";
import { SimpleLoader } from "../Layout/Loader";
import DashboardTableScroll from "../shared/DashboardTableScroll";
import OrderUpdatesPanel from "../Profile/OrderUpdatesPanel";

const MAX_GRID_ROWS = 200;

const ORDER_LIST_TYPES = new Set([...ORDER_NOTIFICATION_TYPES, ...SELLER_REFUND_REVIEW_TYPES]);

const statusColors = {
    Completed: "bg-green-100 text-green-800",
    Delivered: "bg-green-100 text-green-800",
    Processing: "bg-blue-100 text-blue-800",
    "Transferred to delivery partner": "bg-purple-100 text-purple-800",
    Shipping: "bg-yellow-100 text-yellow-800",
    Received: "bg-indigo-100 text-indigo-800",
    "On the way": "bg-orange-100 text-orange-800",
    Approved: "bg-emerald-100 text-emerald-800",
    Declined: "bg-rose-100 text-rose-800",
    "Resolved (no refund)": "bg-indigo-100 text-indigo-800",
    "Under review": "bg-amber-100 text-amber-800",
    "VA mediation": "bg-[#e8f0ea] text-[#38513b]",
};

const AllOrders = () => {
    const { shopOrders, isLoading } = useSelector((state) => state.order);
    const { user } = useSelector((state) => state.user);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const awaitingConfirmationOnly = searchParams.get("awaitingConfirmation") === "1";
    const dispatch = useDispatch();
    const { notifications } = useNotifications();
    useMarkNotificationsReadOnPage("seller_order_list");

    const orderUnreadMap = useMemo(() => {
        const m = new Map();
        if (!Array.isArray(notifications)) return m;
        notifications.forEach((n) => {
            if (n.read || !ORDER_LIST_TYPES.has(n.type)) return;
            const isRefundReview = SELLER_REFUND_REVIEW_TYPES.includes(n.type);
            if (!isRefundReview && !isSellerFacingOrderNotification(n)) return;
            const id = getNotificationRelatedOrderId(n);
            if (!id) return;
            const prev = m.get(id) || { count: 0, title: "" };
            m.set(id, {
                count: prev.count + 1,
                title: prev.title || n.title || n.message || "Update",
            });
        });
        return m;
    }, [notifications]);

    const [sellerFetchSent, setSellerFetchSent] = useState(false);
    const [gridReady, setGridReady] = useState(false);
    const prevUserIdRef = useRef(null);
    const isFetchingRef = useRef(false);
    
    useEffect(() => {
        const id = requestAnimationFrame(() => setGridReady(true));
        return () => cancelAnimationFrame(id);
    }, []);
    
    useEffect(() => {
        const currentUserId = user?._id;
        const isSeller = user?.isSeller;
        
        if (!currentUserId || !isSeller || currentUserId === prevUserIdRef.current || isFetchingRef.current) {
            return;
        }
        prevUserIdRef.current = currentUserId;
        isFetchingRef.current = true;
        setSellerFetchSent(true);
        
        dispatch(getAllOrdersOfShop(currentUserId)).finally(() => {
            isFetchingRef.current = false;
        });
    }, [dispatch, user?._id, user?.isSeller]);
    
    const columns = useMemo(() => [
        {
            field: "orderId",
            headerName: "Order ID",
            minWidth: 160,
            flex: 0.8,
            renderCell: (params) => {
                const rowId = params.id?.toString?.() || params.row?.id?.toString?.() || "";
                const fromMap = orderUnreadMap.get(rowId);
                const unreadCount =
                    Number(fromMap?.count) || Number(params.row?.unreadCount) || 0;
                const hasUnread = unreadCount > 0;
                return (
                    <div
                        className={`flex min-w-0 items-center gap-1.5 ${
                            hasUnread ? "rounded-md bg-red-50 px-1.5 py-1" : ""
                        }`}
                    >
                        {hasUnread ? (
                            <span className="inline-flex shrink-0 items-center rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                New
                            </span>
                        ) : null}
                        <span
                            className={`font-mono text-xs ${
                                hasUnread ? "font-semibold text-red-700" : "text-slate-600"
                            }`}
                        >
                            {params.value}
                        </span>
                    </div>
                );
            },
        },
        {
            field: "customerName",
            headerName: "Customer Name",
            minWidth: 180,
            flex: 1,
            renderCell: (params) => (
                <span className="font-medium text-slate-900">{params.value || "N/A"}</span>
            ),
        },
        {
            field: "createdAt",
            headerName: "Order Date",
            minWidth: 145,
            flex: 0.8,
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 150,
            flex: 0.8,
            renderCell: (params) => {
                const statusLabel = params.row.statusLabel || params.value || "";
                const rowId = params.id?.toString?.() || params.row?.id?.toString?.() || "";
                const unreadCount =
                    Number(orderUnreadMap.get(rowId)?.count) ||
                    Number(params.row?.unreadCount) ||
                    0;
                const colorClass =
                  statusColors[statusLabel] ||
                  (isOrderListStatusSuccess(statusLabel)
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800");
                return (
                    <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${colorClass}`}>
                            {statusLabel}
                        </span>
                        {unreadCount > 0 && (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
                                {unreadCount > 99 ? "99+" : unreadCount}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            field: "ukaraStatus",
            headerName: "UKARA",
            minWidth: 140,
            flex: 0.8,
            renderCell: (params) => {
                const value = params.value || "pending";
                const label =
                    value === "verified"
                        ? "Verified"
                        : value === "rejected"
                            ? "Rejected"
                            : "Pending";
                const className =
                    value === "verified"
                        ? "bg-emerald-100 text-emerald-700"
                        : value === "rejected"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-amber-100 text-amber-700";
                return (
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}>
                        {label}
                    </span>
                );
            },
        },
        {
            field: "itemsQty",
            headerName: "Items Qty",
            type: "number",
            minWidth: 140,
            flex: 0.7,
        },
        {
            field: "trackingStatus",
            headerName: "Tracking",
            minWidth: 135,
            flex: 0.8,
            renderCell: (params) => {
                const value = params.value || "pending";
                const label = value === "active" ? "Active" : "Pending";
                const className =
                    value === "active"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-700";
                return (
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}>
                        {label}
                    </span>
                );
            },
        },
        {
            field: "total",
            headerName: "Total",
            type: "number",
            minWidth: 130,
            flex: 0.8,
        },

        {
            field: "actions",
            flex: 1,
            minWidth: 150,
            headerName: "",
            sortable: false,
            renderCell: (params) => {
                return (
                    <>
                        <Link
                            to={`/profile/seller-order/${params.id}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                primeMobileDashboardContentScroll(`/profile/seller-order/${params.id}`);
                            }}
                        >
                            <Button>
                                <AiOutlineArrowRight size={20} />
                            </Button>
                        </Link>
                    </>
                );
            },
        },
    ], [orderUnreadMap]);

    const row = useMemo(() => {
        if (!gridReady || !shopOrders || !Array.isArray(shopOrders)) return [];
        let list = shopOrders;
        if (awaitingConfirmationOnly) {
            list = list.filter(
                (item) => item?.payoutReleased !== true && !item?.userReceivedConfirmed
            );
        }
        const sorted = [...list.slice(0, MAX_GRID_ROWS)].sort((a, b) => {
            const aUnread = orderUnreadMap.get(a._id?.toString())?.count || 0;
            const bUnread = orderUnreadMap.get(b._id?.toString())?.count || 0;
            if (bUnread !== aUnread) return bUnread - aUnread;
            const ta = new Date(a?.createdAt || 0).getTime();
            const tb = new Date(b?.createdAt || 0).getTime();
            return tb - ta;
        });
        return sorted.map((item) => {
            const id = item._id?.toString();
            const unread = orderUnreadMap.get(id);
            return {
                id: item._id,
                orderId: item?.orderNumber ?? item?._id?.toString().substring(0, 8) ?? "N/A",
                customerName: item?.user?.name || "N/A",
                createdAt: item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "",
                itemsQty: item.cart?.length || 0,
                total: "US$ " + item.totalPrice,
                status: getOrderListStatusLabel(item),
                statusLabel: getOrderListStatusLabel(item),
                ukaraStatus: item.ukaraStatus,
                trackingStatus: item.trackingStatus,
                unreadCount: unread?.count || 0,
            };
        });
    }, [shopOrders, gridReady, orderUnreadMap, awaitingConfirmationOnly]);

    const unreadOrderNotifications = useMemo(() => {
        if (!Array.isArray(notifications)) return [];
        return notifications
            .filter((n) => {
                if (n.read || !ORDER_LIST_TYPES.has(n.type)) return false;
                const isRefundReview = SELLER_REFUND_REVIEW_TYPES.includes(n.type);
                return isRefundReview || isSellerFacingOrderNotification(n);
            })
            .sort(
                (a, b) =>
                    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
            );
    }, [notifications]);

    const filteredOrderCount = awaitingConfirmationOnly ? row.length : null;
    const truncated = Array.isArray(shopOrders) && shopOrders.length > MAX_GRID_ROWS && !awaitingConfirmationOnly;

    // Memoize handleRowClick to prevent unnecessary re-renders
    const handleRowClick = useCallback((params) => {
        navigateWithMobileDashboardScroll(
            navigate,
            `/profile/seller-order/${params.id}`
        );
    }, [navigate]);

    const showLoader = !sellerFetchSent || isLoading;
    const showGrid = !showLoader && gridReady;
    return (
        <>
            {showLoader ? (
                <SimpleLoader />
            ) : !gridReady ? (
                <SimpleLoader />
            ) : (
                <div className="w-full min-w-0 space-y-3">
                    <OrderUpdatesPanel notifications={unreadOrderNotifications} sellerView />
                    <div className="w-full min-w-0 rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                        <h2 className="text-xl font-semibold text-slate-900">
                            {awaitingConfirmationOnly ? "Orders awaiting confirmation" : "Review Orders"}
                        </h2>
                        <p className="text-sm text-slate-600 mt-1">
                            {awaitingConfirmationOnly
                              ? "These sales are held in escrow until the buyer confirms delivery."
                              : "Manage and track all orders received from customers."}
                            {awaitingConfirmationOnly ? (
                              <>
                                {" "}
                                <Link
                                  to="/profile/orders?view=selling"
                                  className="font-semibold text-[#38513b] underline hover:text-[#2f4232]"
                                >
                                  Show all selling orders
                                </Link>
                              </>
                            ) : null}
                            {truncated && (
                                <span className="block mt-1 text-amber-700">
                                    Showing first {MAX_GRID_ROWS} of {shopOrders.length} orders.
                                </span>
                            )}
                            {awaitingConfirmationOnly && filteredOrderCount === 0 ? (
                              <span className="block mt-1 text-slate-500">
                                No orders are currently awaiting buyer confirmation.
                              </span>
                            ) : null}
                        </p>
                    </div>
                    <div className="p-4">
                      <DashboardTableScroll>
                        <div className="min-w-[680px]">
                          <DataGrid
                            rows={row}
                            columns={columns}
                            pageSize={10}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            disableSelectionOnClick
                            autoHeight
                            initialState={{ sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] } }}
                            onRowClick={handleRowClick}
                            sx={{
                              border: "none",
                              "& .MuiDataGrid-cell": {
                                borderBottom: "1px solid #e2e8f0",
                              },
                              "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#1e293b",
                                borderBottom: "2px solid #334155",
                                fontWeight: 700,
                                fontSize: "0.875rem",
                                color: "#ffffff",
                                "& .MuiDataGrid-columnHeaderTitle": {
                                  fontWeight: 700,
                                  fontSize: "0.875rem",
                                  color: "#ffffff",
                                },
                              },
                              "& .MuiDataGrid-row": {
                                cursor: "pointer",
                              },
                              "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f8fafc",
                              },
                              "& .order-row-has-update": {
                                backgroundColor: "#fef2f2",
                              },
                              "& .order-row-has-update:hover": {
                                backgroundColor: "#fee2e2",
                              },
                            }}
                            getRowClassName={(params) =>
                              (params.row?.unreadCount || 0) > 0 ? "order-row-has-update" : ""
                            }
                          />
                        </div>
                      </DashboardTableScroll>
                    </div>
                </div>
                </div>
            )}
        </>
    );
};

export default AllOrders;